import {
  Box,
  Button,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Divider,
} from "@mui/material";
import { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { http } from "../../../api/http";

interface ClinicianGlobalUpdateFormProps {
  clinicianId: number;
  clinicianName: string;
  onClose: () => void;
  /** When set, only these services are updated (service-wise update) */
  serviceIds?: number[];
  serviceName?: string;
}

export const ClinicianGlobalUpdateForm = ({
  clinicianId,
  clinicianName,
  onClose,
  serviceIds,
  serviceName,
}: ClinicianGlobalUpdateFormProps) => {
  const queryClient = useQueryClient();
  const [audioPrice, setAudioPrice] = useState<number>(0);
  const [videoPrice, setVideoPrice] = useState<number>(0);
  const [homePrice, setHomePrice] = useState<number>(0);
  const [overrideExisting, setOverrideExisting] = useState(true);
  const [createMissing, setCreateMissing] = useState(true);

  const { mutate: updateGlobalPricing, isLoading } = useMutation(
    async (data: any) => {
      return http.post(
        `/admin/pricing/clinician/${clinicianId}/global-update`,
        data
      );
    },
    {
      onSuccess: (res: any) => {
        queryClient.invalidateQueries("pricing");
        queryClient.invalidateQueries("pricing-stats");
        queryClient.invalidateQueries(["clinician-pricing", String(clinicianId)]);
        queryClient.invalidateQueries(["clinician-pricing", clinicianId]);
        toast.success(res?.data?.message || "Pricing updated successfully");
        onClose();
      },
      onError: (res: any) => {
        toast.error(res?.response?.data?.message || "Failed to update pricing");
      },
    }
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Audio & video are required; home may be 0 when the clinician does not offer home visits
    if (audioPrice <= 0 || videoPrice <= 0) {
      toast.error("Audio and video prices must be greater than 0");
      return;
    }

    if (homePrice < 0) {
      toast.error("Home price cannot be negative");
      return;
    }

    updateGlobalPricing({
      audioModePrice: audioPrice,
      videoModePrice: videoPrice,
      homeModePrice: homePrice,
      overrideExisting,
      createMissing,
      ...(serviceIds?.length ? { serviceIds } : {}),
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Typography variant="h6" mb={1}>
        {serviceName ? "Service Pricing Update" : "Global Pricing Update"}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {serviceName ? (
          <>
            Update pricing for <strong>{serviceName}</strong> · {clinicianName}
          </>
        ) : (
          <>
            Update all service pricing for: <strong>{clinicianName}</strong>
          </>
        )}
      </Typography>

      <Divider sx={{ mb: 3 }} />

      <TextField
        fullWidth
        label="Audio Mode Price (SAR)"
        type="number"
        value={audioPrice}
        onChange={(e) => setAudioPrice(parseFloat(e.target.value) || 0)}
        inputProps={{ min: 0, step: 0.01 }}
        required
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Video Mode Price (SAR)"
        type="number"
        value={videoPrice}
        onChange={(e) => setVideoPrice(parseFloat(e.target.value) || 0)}
        inputProps={{ min: 0, step: 0.01 }}
        required
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Home Mode Price (SAR)"
        type="number"
        value={homePrice}
        onChange={(e) => setHomePrice(parseFloat(e.target.value) || 0)}
        inputProps={{ min: 0, step: 0.01 }}
        helperText="Leave 0 if this clinician does not offer home visits"
        sx={{ mb: 3 }}
      />

      <Divider sx={{ mb: 2 }} />

      <FormControlLabel
        control={
          <Checkbox
            checked={overrideExisting}
            onChange={(e) => setOverrideExisting(e.target.checked)}
          />
        }
        label="Override existing pricing"
        sx={{ mb: 1 }}
      />
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        mb={2}
      >
        If checked, will update existing pricing configurations
      </Typography>

      <FormControlLabel
        control={
          <Checkbox
            checked={createMissing}
            onChange={(e) => setCreateMissing(e.target.checked)}
          />
        }
        label="Create missing pricing"
        sx={{ mb: 1 }}
      />
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        mb={3}
      >
        If checked, will create pricing for services that don't have one yet
      </Typography>

      <Box display="flex" gap={2}>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={24} /> : "Update All"}
        </Button>
        <Button variant="outlined" fullWidth onClick={onClose}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};
